/**
 * One global, throttled "too many requests" notice.
 *
 * Wired into the axios response interceptor so a 429 is always visible to the
 * user no matter which screen triggered it. Throttled and keyed to a single
 * notification because a breached limit rejects every in-flight request — the
 * 2026-09-08 incident produced 509 rejections in 47 minutes, which would
 * otherwise have been 509 toasts.
 */
import { notification } from 'antd';

import { getRateLimitInfo, rateLimitMessage } from './rateLimit';

const NOTIFICATION_KEY = 'peak-rate-limited';
const THROTTLE_MS = 30_000;

let lastShownMs = 0;

/**
 * Show the rate-limit notice if one isn't already on screen.
 * No-ops for any error that isn't a 429.
 */
export function notifyIfRateLimited(error: unknown): void {
  const info = getRateLimitInfo(error);
  if (!info) return;

  const now = Date.now();
  if (now - lastShownMs < THROTTLE_MS) return;
  lastShownMs = now;

  notification.warning({
    key: NOTIFICATION_KEY,
    message: 'Too many requests',
    description: rateLimitMessage(info),
    duration: 10,
    placement: 'topRight',
  });
}

/** Test seam — clears the throttle window. */
export function resetRateLimitNotice(): void {
  lastShownMs = 0;
}
